import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  NumberParam,
  StringParam,
  JsonParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import {
  CollaboratorsIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
  ChevronDownIcon,
} from "../components/icons";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import { Tag, TagCloseButton, TagLabel } from "@chakra-ui/react";
import {
  Container,
  EmptyState,
  Link,
  ScreenContainer,
  ScreenHeader,
  ButtonGroup,
  Button,
} from "../components/wombat-ui";
import { H6 } from "../components/wombat-ui/components/Typography";
import GroupTag from "../components/groups/GroupTag";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import UserAvatar from "../components/utils/avatars/UserAvatar";
import SearchPopover from "../components/utils/SearchPopover";
import SearchPopoverHeading from "../components/utils/SearchPopoverHeading";
import SearchPopoverItem from "../components/utils/SearchPopoverItem";
import { useDebounce } from "react-use";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import {
  GroupFragment,
  SkillFragment,
  useGetGroup,
  useGetSkillsLazyQuery,
  useGetUsersMinimal,
  UserSummaryFragment,
} from "../graphql/generated/definitions";

export type User = {
  avatar: string;
  name: string;
  reviewsAsCollaborator: any[];
  id: string;
  slug: string;
};

type UserOverviewListProps = {
  user: UserSummaryFragment;
  size?: "auto" | "small" | "large";
  loading?: boolean;
};

const UserOverviewList: React.FC<UserOverviewListProps> = ({ user }) => {
  return (
    <li
      className="flex flex-col py-4 pt-2 sm:flex-row sm:justify-between"
      id={`userItem.${user.id}`}
    >
      <div className="flex items-center space-x-4">
        <UserAvatar
          size={8}
          src={user.avatar}
          alt={`${user.name} profile picture`}
        />
        <div className="flex-1 min-w-0 space-x-2">
          <Link
            to={`/users/${user.slug}`}
            id={`${user.name}-link`}
            className="text-sm font-medium truncate"
          >
            {user.name}
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap max-w-lg min-w-0 gap-2 sm:justify-end">
        {user.groups.map((group: GroupFragment) => (
          <div className="flex items-center" key={group.id}>
            <GroupTag group={group} />
          </div>
        ))}
      </div>
    </li>
  );
};

const UserList: React.FC = () => {
  const t = useTranslations();

  const [
    { page, q, sortBy, direction, groupId, havingAnswersMatching },
    setQuery,
  ] = useQueryParams({
    page: NumberParam,
    sortBy: withDefault(StringParam, "name"),
    direction: StringParam,
    q: StringParam,
    groupId: StringParam,
    havingAnswersMatching: withDefault(JsonParam, []),
  });

  const { data, loading } = useGetUsersMinimal({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      groupId: groupId || undefined,
      sortBy: sortBy,
      direction: direction as "ASC" | "DESC",
      havingAnswersMatching,
    },
    fetchPolicy: "cache-and-network",
  });

  const users = data?.users?.collection || [];
  const itemsCount = data?.users?.metadata?.totalCount;

  const [search, setSearch] = useState(q);

  const [skillSearch, setSkillSearch] = useState(q);

  const [getSkills, { data: getSkillsData }] = useGetSkillsLazyQuery();
  const skills = getSkillsData?.skills?.collection || [];

  useDebounce(
    () => {
      getSkills({
        variables: {
          q: skillSearch,
          per: 30,
        },
      });
    },
    500,
    [getSkills, skillSearch]
  );

  const handleSkillSelected = useCallback(
    ({ id }) => {
      setQuery({
        havingAnswersMatching: [...havingAnswersMatching, { skillId: id }],
      });
    },
    [havingAnswersMatching]
  );

  const [getFilteredSkills, { data: getFilteredSkillsData }] =
    useGetSkillsLazyQuery();
  const filteredSkills = getFilteredSkillsData?.skills?.collection || [];

  const filteredSkillIds = useMemo(
    () => havingAnswersMatching.map(({ skillId }) => skillId),
    [havingAnswersMatching]
  );

  useEffect(() => {
    getFilteredSkills({
      variables: {
        idsStrict: filteredSkillIds,
      },
    });
  }, [filteredSkillIds]);

  const { data: selectedGroupData } = useGetGroup({
    variables: {
      id: groupId,
    },
    skip: !groupId,
  });
  const selectedGroup = selectedGroupData?.group;

  return (
    <>
      <List
        isLoading={loading}
        emptyMessage={t("users.noMatchingUsers") as string}
        data={users}
        listOnly
        renderAsList={(data) => <UserOverviewList user={data} key={data.id} />}
        id="users-list"
      >
        <div className="flex flex-col flex-wrap w-full max-w-full gap-2">
          <div className="flex flex-wrap justify-between gap-2 sm:gap-0">
            <SearchInput
              onSubmit={(q) => setQuery({ page: 1, q })}
              value={search}
              onChange={setSearch}
              label={t("list.search") as string}
              placeholder={t("users.searchPlaceholder") as string}
            />
            <div className="flex space-x-3">
              <Link id="groupListButton" variant="button-subtle" to="/groups">
                {t("groups.list")}
              </Link>

              <SearchPopover
                title={
                  <SearchPopoverHeading>
                    {t("professionSkillLibrary.skills")}
                  </SearchPopoverHeading>
                }
                loading={false}
                items={skills}
                onItemSelected={handleSkillSelected}
                onSearchChange={setSkillSearch}
                searchValue={skillSearch}
                button={
                  <Button variant="subtle" trailingIcon={ChevronDownIcon}>
                    {t("users.filterBySkills")}
                  </Button>
                }
                noMatch={
                  <SearchPopoverItem>
                    {t("wombat.select.emptyResult")}
                  </SearchPopoverItem>
                }
                renderItem={(item: SkillFragment) => (
                  <SearchPopoverItem>{item.name}</SearchPopoverItem>
                )}
              />

              <ButtonGroup scale="sm">
                <Button
                  icon={SortAscendingDirectionIcon}
                  onClick={() => setQuery({ direction: "ASC" })}
                  variant={
                    direction === "ASC" || !direction ? "primary" : "subtle"
                  }
                />
                <Button
                  icon={SortDescendingDirectionIcon}
                  onClick={() => setQuery({ direction: "DESC" })}
                  variant={direction === "DESC" ? "primary" : "subtle"}
                />
              </ButtonGroup>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedGroup && (
              <Tag>
                <TagLabel>{selectedGroup.name}</TagLabel>
                <TagCloseButton
                  onClick={() => setQuery({ page: 1, groupId: undefined })}
                />
              </Tag>
            )}
            {filteredSkills.map(({ name, id }) => (
              <Tag key={id}>
                <TagLabel>{name}</TagLabel>
                <TagCloseButton
                  onClick={() =>
                    setQuery({
                      page: 1,
                      havingAnswersMatching: havingAnswersMatching.filter(
                        ({ skillId }) => id !== skillId
                      ),
                    })
                  }
                />
              </Tag>
            ))}
          </div>
        </div>
      </List>
      {!loading && (
        <Pagination
          page={page}
          onChange={(page) => setQuery({ page })}
          itemsCount={itemsCount}
          itemsPerPage={20}
        />
      )}
    </>
  );
};

const UserListScreen: React.FC = () => {
  const t = useTranslations();

  const { isCapableOfInsertingUsers, isCapableOfReadingUsers } = useViewer();

  useBreadcrumbs([
    {
      to: "/users",
      text: t("users.header") as string,
    },
  ]);

  return (
    <ScreenContainer
      error={
        !isCapableOfReadingUsers ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={t("users.header") as string}
        description={t("users.usersDescription") as string}
        icon={CollaboratorsIcon}
        actions={[
          isCapableOfInsertingUsers && (
            <Link variant="button" to="/users/new" key="new">
              {t("users.addUsers")}
            </Link>
          ),
        ]}
      />
      <Container className="flex flex-col space-y-10">
        <UserList />
      </Container>
    </ScreenContainer>
  );
};

export default UserListScreen;
